import React, { useCallback, useEffect } from "react";
import { Provider } from "react-redux";
import { store } from "./store";
import AuthPage from "../pages/AuthPage";
import { AccueilPage } from "../pages/Home"; // ← si tu exporte AccueilPage depuis Home

// ── Import de toutes tes pages ────────────────────────────────────────────────
// Remplace ces imports par tes vrais chemins existants

import ProfilePage from "../pages/ProfilePage";
import DashboardPage from "../pages/DashboardPage";
import { isAdminGeneral, Page } from "../layout/AppBar";
import QueryBuilder from "../features/cube/components/QueryBuilder";
import CouleurView from "../features/dimension-analysis/views/CouleurView";
import MagasinView from "../features/dimension-analysis/views/MagasinView";
import VentesView from "../features/dimension-analysis/views/VentesView";
import SousFamilleView from "../features/dimension-analysis/views/SousFamilleView";
import SaisonView from "../features/dimension-analysis/views/SaisonView";
import GroupeView from "../features/dimension-analysis/views/GroupeView";
import UniversView from "../features/dimension-analysis/views/UniversView";
import CouleurTailleView from "../features/dimension-analysis/views/CouleurTailleView";
import FamilleView from "../features/dimension-analysis/views/FamilleView";
import VendeurView from "../features/dimension-analysis/views/VendeurView";
import TailleView from "../features/dimension-analysis/views/TailleView";
import Layout from "../layout/Layout";
import MarqueView from "../features/dimension-analysis/views/MarqueView"; // ← nom corrigé
import AIQueryBuilder from "../features/forecast/components/AIQueryBuilder";
import IAFamilleView from "../features/forecast/components/IAFamilleView";
import PrevisionFContainer from "../features/forecast/components/PrevisionFContainer";
import UsersPage from "../features/users/components/UsersPage";
import IASousFamilleView from "../features/forecast/components/IASousFamilleView";
import { logout, restoreSession } from "../features/auth/authSlice";
import { setUnauthorizedHandler } from "../shared/api/clients";
import { useAppDispatch, useAppSelector } from "../shared/store/hooks";
import { ServerUnreachable, SessionChecking } from "./SessionScreens";

function AppShell() {
  const dispatch = useAppDispatch();
  const { status, role: userRole } = useAppSelector((s) => s.auth);

  // Au chargement (F5 compris) : un token stocké est revérifié au lieu d'être ignoré
  useEffect(() => {
    if (status === "checking") dispatch(restoreSession());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Les onglets (nw_tabs_list) sont conservés → restaurés à la reconnexion
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  // Tout 401 sur une requête authentifiée (token expiré/révoqué) → déconnexion
  useEffect(() => {
    setUnauthorizedHandler(handleLogout);
    return () => setUnauthorizedHandler(null);
  }, [handleLogout]);

  // ── Rendu de la page selon l'onglet actif ─────────────────────────────────
  const renderPage = (page: Page, openTab: (p: Page) => void) => {
    switch (page) {
      case "accueil":
        return <AccueilPage onNavigate={openTab} />; // ← setPage → onNavigate

      case "dashboard":
        return <DashboardPage />;
      case "mdx":
        return <QueryBuilder />;
      case "ai_query":
        return <AIQueryBuilder />;
      case "ia_famille":
        return <IAFamilleView />;
      case "couleur":
        return <CouleurView />;
    case "ia_sous_famille":
  return <IASousFamilleView />;
      case "magazin":
        if (!isAdminGeneral(userRole)) {
          return (
            <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>
              Accès réservé à l'Admin Général.
            </div>
          );
        }
        return <MagasinView />;
      case "marques":
        return <MarqueView />; // ← MarqueView sans "s"
      case "ventes":
        return <VentesView />;
      case "sousfamille":
        return <SousFamilleView />;
      case "saison":
        return <SaisonView />;
      case "groupe":
        return <GroupeView />;
      case "univers":
        return <UniversView />;
      case "attribution_role":
        return <UsersPage />;
      case "taille":
        return <TailleView />;
      case "couleur-taille":
        return <CouleurTailleView />;

      case "famille":
        return <FamilleView />;
      case "vendeur":
        return <VendeurView />;

      case "prevision_f":
        return <PrevisionFContainer />;
      case "profile":
        return <ProfilePage onLogout={handleLogout} />;
      default:
        return <AccueilPage onNavigate={openTab} />; // ← setPage → onNavigate
    }
  };
  switch (status) {
    case "checking":
      return <SessionChecking />;
    case "unreachable":
      return (
        <ServerUnreachable
          onRetry={() => dispatch(restoreSession())}
          onLogout={handleLogout}
        />
      );
    case "anonymous":
      // AuthPage enregistre le token ; la session est ensuite vérifiée comme au démarrage
      return <AuthPage onLogin={() => dispatch(restoreSession())} />;
    case "authenticated":
      return (
        <Layout
          renderPage={renderPage}
          onLogout={handleLogout}
          userRole={userRole}
        />
      );
  }
}

function App() {
  return (
    <Provider store={store}>
      <AppShell />
    </Provider>
  );
}

export default App;
